import React from 'react';
import { EDSButton } from '@gehc-eds-react/eds-button';
import { EDSTextField } from '@gehc-eds-react/eds-text-field';
import { EDSModal } from '@gehc-eds-react/eds-modal';
 
const readValue = (value) => {
    if (value === null || value === undefined || value === '') return '--';
    return value;
};

const getCurrentDateTimeLocalMax = () => {
    const now = new Date();
    const timezoneOffsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
};

const splitDateTimeLocalValue = (value) => {
    if (!value) {
        return { datePart: '', timePart: '' };
    }

    const [datePart = '', timePart = ''] = value.split('T');

    return {
        datePart,
        timePart: timePart.slice(0, 5)
    };
};

const buildDateTimeLocalValue = (datePart, timePart) => {
    if (!datePart) {
        return '';
    }

    if (!timePart) {
        return `${datePart}T`;
    }

    return `${datePart}T${timePart}`;
};

const buildTimeOptions = () => Array.from({ length: 24 * 60 }, (_, index) => {
    const hours = String(Math.floor(index / 60)).padStart(2, '0');
    const minutes = String(index % 60).padStart(2, '0');

    return `${hours}:${minutes}`;
});
 
/**
* "Create Job" modal for SourceDetailsPage. Purely presentational — all form
* state (jobMode, jobReplicatePrefix, ...), validation errors, and the
* save/close handlers are owned by the parent and passed in as props.
*/
export const CreateJobModal = ({
    isOpen,
    isCreatingJob,
    resolvedSourceId,
    jobAccountId,
    jobMode,
    setJobMode,
    jobReplicatePrefix,
    setJobReplicatePrefix,
    jobReplicatePrefixError,
    setJobReplicatePrefixError,
    jobKeySuffix,
    setJobKeySuffix,
    jobCreatedAfter,
    setJobCreatedAfter,
    jobPrefixesText,
    setJobPrefixesText,
    jobPrefixesError,
    setJobPrefixesError,
    jobDatasetName,
    setJobDatasetName,
    jobDatasetNameError,
    setJobDatasetNameError,
    jobDescription,
    setJobDescription,
    onClose,
    onSave
}) => {
    const currentDateTimeLocalMax = getCurrentDateTimeLocalMax();
    const { datePart: currentDatePart, timePart: currentTimePart } = splitDateTimeLocalValue(currentDateTimeLocalMax);
    const { datePart: selectedDatePart, timePart: selectedTimePart } = splitDateTimeLocalValue(jobCreatedAfter);
    const timeOptions = buildTimeOptions();

    const handleCreatedAfterDateChange = (nextDatePart) => {
        const nextTimePart = nextDatePart === currentDatePart && selectedTimePart > currentTimePart
            ? currentTimePart
            : selectedTimePart;

        setJobCreatedAfter(buildDateTimeLocalValue(nextDatePart, nextTimePart));
    };

    const handleCreatedAfterTimeChange = (nextTimePart) => {
        const normalizedTimePart = selectedDatePart === currentDatePart && nextTimePart > currentTimePart
            ? currentTimePart
            : nextTimePart;

        setJobCreatedAfter(buildDateTimeLocalValue(selectedDatePart, normalizedTimePart));
    };

    return (
        <EDSModal
            variant="standard"
            isOpen={isOpen}
            headerConfig={{ text: 'Create Job', useCancelIcon: true }}
            width="660px"
            onEDSModalClose={onClose}
            content={<div slot="main-content" style={{ padding: '28px 32px', maxHeight: '65vh', overflowY: 'auto' }}>
                        <div
                            style={{
                                fontSize: '11px',
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: '#9ca3af',
                                marginBottom: '14px'
                            }}
                        >
                            Source
                        </div>
 
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '18px', marginBottom: '8px' }}>
                            <EDSTextField
                                name="create-job-source-id"
                                label={{ width: '', position: 'top', text: 'Source ID' }}
                                value={readValue(resolvedSourceId)}
                                width="100%"
                                type="text"
                                isDisabled={true}
                            />
                        </div>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '18px',
                                marginTop: '18px'
                            }}
                        >
                            <EDSTextField
                                name="create-job-account-id"
                                label={{ width: '', position: 'top', text: 'Account Number' }}
                                value={jobAccountId}
                                placeholder="Enter account number"
                                isDisabled={true}
                                width="100%"
                                type="text"
                                maxLength={12}
                            />
                        </div>
 
    {/* Filter / Prefix mode selector */}
                        <div
                            style={{
                                fontSize: '11px',
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: '#9ca3af',
                                margin: '28px 0 14px 0'
                            }}
                        >
                            Job Type
                        </div>
                        <div
                            role="tablist"
                            aria-label="Job creation mode"
                            style={{
                                display: 'inline-flex',
                                border: '1.5px solid #dde1e7',
                                borderRadius: '999px',
                                padding: '3px',
                                gap: '2px'
                            }}
                        >
                            {[
                                { key: 'filter', label: 'Filter' },
                                { key: 'prefix', label: 'Prefix' }
                            ].map((option) => (
                                <button
                                    key={option.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={jobMode === option.key}
                                    onClick={() => {
                                        setJobMode(option.key);
                                        setJobReplicatePrefixError('');
                                        setJobPrefixesError('');
                                        setJobDatasetNameError('');
                                    }}
                                    style={{
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '7px 20px',
                                        borderRadius: '999px',
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        backgroundColor: jobMode === option.key ? '#6D55B6' : 'transparent',
                                        color: jobMode === option.key ? '#fff' : '#4b5563',
                                        transition: 'background-color 0.15s ease, color 0.15s ease'
                                    }}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
 
                        <div style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280', lineHeight: 1.45 }}>
                            <div><strong>Filter:</strong> Bulk under one prefix, any scale</div>
                            <div><strong>Prefixes:</strong> Multiple non-adjacent folders in one job</div>
                        </div>
 
                        {jobMode === 'filter' ? (
                            <>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                        gap: '18px',
                                        marginTop: '18px'
                                    }}
                                >
                                    <div>
                                        <EDSTextField
                                            name="create-job-replicate-prefix"
                                            label={{ width: '', position: 'top', text: 'Prefix' }}
                                            value={jobReplicatePrefix}
                                            placeholder="Enter prefix"
                                            isRequired={true}
                                            validationError={jobReplicatePrefixError}
                                            width="100%"
                                            type="text"
                                            onEDSTextFieldChange={(e) => {
                                                setJobReplicatePrefix(e.target.value);
                                                if (jobReplicatePrefixError) setJobReplicatePrefixError('');
                                            }}
                                        />
                                        {jobReplicatePrefixError && (
                                            <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
                                                {jobReplicatePrefixError}
                                            </div>
                                        )}
                                    </div>
 
                                    <EDSTextField
                                        name="create-job-key-suffix"
                                        label={{ width: '', position: 'top', text: 'Key Suffix' }}
                                        value={jobKeySuffix}
                                        placeholder=".zip"
                                        isRequired={false}
                                        width="100%"
                                        type="text"
                                        onEDSTextFieldChange={(e) => setJobKeySuffix(e.target.value)}
                                    />
                                </div>
 
                                <div
                                    style={{
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                        textTransform: 'uppercase',
                                        color: '#9ca3af',
                                        margin: '28px 0 14px 0'
                                    }}
                                >
                                    Job Options
                                </div>
 
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(2, 1fr)',
                                        gap: '18px',
                                        alignItems: 'end'
                                    }}
                                >
                                    <div>
                                        <label
                                            htmlFor="create-job-created-after-date"
                                            style={{
                                                display: 'block',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: '#111827',
                                                marginBottom: '8px'
                                            }}
                                        >
                                            Created After
                                        </label>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '12px' }}>
                                            <input
                                                id="create-job-created-after-date"
                                                aria-label="Created Date"
                                                type="date"
                                                name="create-job-created-after-date"
                                                value={selectedDatePart}
                                                max={currentDatePart}
                                                onChange={(e) => handleCreatedAfterDateChange(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px 12px',
                                                    border: '1.5px solid #dde1e7',
                                                    borderRadius: '8px',
                                                    fontSize: '14px',
                                                    color: '#111827',
                                                    boxSizing: 'border-box',
                                                    outline: 'none',
                                                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                                    fontFamily: 'inherit'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#0091DA';
                                                    e.target.style.boxShadow = '0 0 0 3px rgba(0, 145, 218, 0.14)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#dde1e7';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                            <select
                                                id="create-job-created-after-time"
                                                aria-label="Created Time"
                                                name="create-job-created-after-time"
                                                value={selectedTimePart}
                                                onChange={(e) => handleCreatedAfterTimeChange(e.target.value)}
                                                disabled={!selectedDatePart}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px 12px',
                                                    border: '1.5px solid #dde1e7',
                                                    borderRadius: '8px',
                                                    fontSize: '14px',
                                                    color: '#111827',
                                                    boxSizing: 'border-box',
                                                    outline: 'none',
                                                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                                    fontFamily: 'inherit',
                                                    backgroundColor: selectedDatePart ? '#ffffff' : '#f3f4f6'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#0091DA';
                                                    e.target.style.boxShadow = '0 0 0 3px rgba(0, 145, 218, 0.14)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#dde1e7';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            >
                                                <option value="">Select time</option>
                                                {timeOptions.map((timeOption) => {
                                                    const isFutureTime = selectedDatePart === currentDatePart && timeOption > currentTimePart;

                                                    return (
                                                        <option key={timeOption} value={timeOption} disabled={isFutureTime}>
                                                            {timeOption}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                                            Only include objects created after this date and time.
                                        </div>
                                    </div>
 

                                </div>
                            </>
                        ) : (
                            <div style={{ marginTop: '18px' }}>
                                <label
                                    htmlFor="create-job-prefixes"
                                    style={{
                                        display: 'block',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: '#111827',
                                        marginBottom: '8px'
                                    }}
                                >
                                    Prefixes <span style={{ color: '#d32f2f' }}>*</span>
                                </label>
                                <textarea
                                    id="create-job-prefixes"
                                    value={jobPrefixesText}
                                    onChange={(e) => {
                                        setJobPrefixesText(e.target.value);
                                        if (jobPrefixesError) setJobPrefixesError('');
                                    }}
                                    placeholder={'batches/2026-01-a/\nbatches/2026-01-b/'}
                                    rows={4}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: jobPrefixesError ? '1.5px solid #d32f2f' : '1.5px solid #dde1e7',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        color: '#111827',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#0091DA';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(0, 145, 218, 0.14)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = jobPrefixesError ? '#d32f2f' : '#dde1e7';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                                {jobPrefixesError ? (
                                    <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '6px' }}>
                                        {jobPrefixesError}
                                    </div>
                                ) : (
                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                                        One prefix per line, e.g. batches/2026-01-a/
                                    </div>
                                )}
 
                                <div style={{ marginTop: '16px' }}>
                                    <label
                                        htmlFor="create-job-prefix-created-after-date"
                                        style={{
                                            display: 'block',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            color: '#111827',
                                            marginBottom: '8px'
                                        }}
                                    >
                                        Created After
                                    </label>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: '12px' }}>
                                        <input
                                            id="create-job-prefix-created-after-date"
                                            aria-label="Created Date"
                                            type="date"
                                            name="create-job-prefix-created-after-date"
                                            value={selectedDatePart}
                                            max={currentDatePart}
                                            onChange={(e) => handleCreatedAfterDateChange(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '1.5px solid #dde1e7',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                color: '#111827',
                                                boxSizing: 'border-box',
                                                outline: 'none',
                                                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                                fontFamily: 'inherit'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#0091DA';
                                                e.target.style.boxShadow = '0 0 0 3px rgba(0, 145, 218, 0.14)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#dde1e7';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        />
                                        <select
                                            id="create-job-prefix-created-after-time"
                                            aria-label="Created Time"
                                            name="create-job-prefix-created-after-time"
                                            value={selectedTimePart}
                                            onChange={(e) => handleCreatedAfterTimeChange(e.target.value)}
                                            disabled={!selectedDatePart}
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '1.5px solid #dde1e7',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                color: '#111827',
                                                boxSizing: 'border-box',
                                                outline: 'none',
                                                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                                fontFamily: 'inherit',
                                                backgroundColor: selectedDatePart ? '#ffffff' : '#f3f4f6'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#0091DA';
                                                e.target.style.boxShadow = '0 0 0 3px rgba(0, 145, 218, 0.14)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#dde1e7';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        >
                                            <option value="">Select time</option>
                                            {timeOptions.map((timeOption) => {
                                                const isFutureTime = selectedDatePart === currentDatePart && timeOption > currentTimePart;

                                                return (
                                                    <option key={timeOption} value={timeOption} disabled={isFutureTime}>
                                                        {timeOption}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                                        Only include objects created after this date and time.
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Dataset Name / Description — shared by both Filter and Prefix modes */}
                        <div style={{ marginTop: '18px' }}>
                            <EDSTextField
                                name="create-job-dataset-name"
                                label={{ width: '', position: 'top', text: 'Dataset Name' }}
                                value={jobDatasetName}
                                placeholder="Enter dataset name"
                                isRequired={true}
                                validationError={jobDatasetNameError}
                                width="100%"
                                type="text"
                                onEDSTextFieldChange={(e) => {
                                    setJobDatasetName(e.target.value);
                                    if (jobDatasetNameError) setJobDatasetNameError('');
                                }}
                            />
                            {jobDatasetNameError && (
                                <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
                                    {jobDatasetNameError}
                                </div>
                            )}
                        </div>
                        <div style={{ marginTop: '18px' }}>
                            <label
                                htmlFor="create-job-description"
                                style={{
                                    display: 'block',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#111827',
                                    marginBottom: '8px'
                                }}
                            >
                                Description
                            </label>
                            <textarea
                                id="create-job-description"
                                value={jobDescription}
                                onChange={(e) => setJobDescription(e.target.value)}
                                placeholder="Describe what this job is for"
                                rows={3}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    border: '1.5px solid #dde1e7',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    color: '#111827',
                                    boxSizing: 'border-box',
                                    outline: 'none',
                                    fontFamily: 'inherit',
                                    resize: 'vertical'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#0091DA';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(0, 145, 218, 0.14)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#dde1e7';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                            <EDSButton
                                variant="primary"
                                type="button"
                                label={isCreatingJob ? 'Creating...' : 'Create Job'}
                                onEDSButtonClick={onSave}
                                isDisabled={isCreatingJob}
                            />
                        </div>
                    </div>
            }
        />
    );
};
 
export default CreateJobModal;

 
 